import { style, WalrusGlobalStyle } from '@do/walrus';
import _ from 'lodash';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import Frame from 'react-styled-frame';
import styled, { ThemeProvider } from 'styled-components';

const StyledFrame = styled(Frame)`
  width: 100%;
  overflow: visible;
`;

const bodyFontFamily =
  'Proxima Nova, proxima-nova, Helvetica Neue, Helvetica, Arial, sans-serif';

const IframeContainer = styled.div`
  ${WalrusGlobalStyle.__stylesheet__}
  font: ${style.vars.fontWeight.base} ${style.vars.fontSize.base} / ${style.vars
    .lineHeight} ${bodyFontFamily};
  text-size-adjust: 100%;

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;

  box-sizing: border-box;
  margin: -5px;

  h1,
  h2 {
    border-bottom: 0 none;
    margin-top: ${style.vars.space['5']};
    font-weight: 600;
    font-size: 1.3rem;
  }
  h3 {
    font-size: 1rem;
  }

  ol,
  ul {
    padding-left: ${style.vars.space['6']};
  }

  p,
  li {
    color: ${style.colors.shadesOfGrey['8']};
  }
`;

type IFrameProps = {
  title: string;
  id: string;
  children: React.ReactNode;
};

const IFrame: React.FC<IFrameProps> = (props) => {
  const ref = useRef(null);

  function handleResize() {
    const frame = document.getElementById(props.id);
    if (frame) {
      frame.style.height = ref?.current?.scrollHeight + 'px';
    }
  }

  useLayoutEffect(() => {
    handleResize();
  });

  useEffect(() => {
    const debouncedResize = _.debounce(handleResize);
    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.current?.scrollHeight]);

  const fontStylesheet = React.createElement('link', {
    rel: 'stylesheet',
    href: 'https://use.typekit.net/ryt7tqg.css'
  });

  return (
    <ThemeProvider theme={{ IframeContainer }}>
      <StyledFrame
        title={props.title}
        scrolling="no"
        onLoad={handleResize}
        contentDidMount={handleResize}
        id={props.id}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        head={fontStylesheet}
      >
        <IframeContainer ref={ref}>{props.children}</IframeContainer>
      </StyledFrame>
    </ThemeProvider>
  );
};

export default IFrame;
